import type { Flatten } from '@root/src/types';
import type { CartQuery } from '@schema/main';

type CartLoyaltyProgram = Flatten<
  NonNullable<CartQuery['cart']['cartLoyaltyProgram']>
>;

type LoyaltyProgram = NonNullable<
  Flatten<CartLoyaltyProgram['loyaltyProgram']>
>;

export interface PrepareGiftToSelectReturnType {
  activeGift: Array<{
    quantity: number;
    id?: number;
    entity?: string;
  }>;
  uniqueProducts: Array<
    Flatten<LoyaltyProgram['productsAsGifts']> & {
      loyaltyItemId: number;
      quantity: number;
    }
  >;
  uniqueServices: Array<
    Flatten<LoyaltyProgram['servicesAsGifts']> & {
      cartLoyaltyProgramId: number;
      quantity: number;
    }
  >;
}

const prepareGiftToSelect = ({
  cartLoyaltyProgram
}: {
  cartLoyaltyProgram: CartLoyaltyProgram[] | null | undefined;
}): PrepareGiftToSelectReturnType => {
  const activeGift: PrepareGiftToSelectReturnType['activeGift'] = [];
  const uniqueProducts: PrepareGiftToSelectReturnType['uniqueProducts'] = [];
  const uniqueServices: PrepareGiftToSelectReturnType['uniqueServices'] = [];

  if (!cartLoyaltyProgram) {
    return {
      activeGift,
      uniqueProducts,
      uniqueServices
    };
  }

  cartLoyaltyProgram?.forEach(loyaltyItem => {
    if (loyaltyItem.loyaltyProgram) {
      loyaltyItem.loyaltyProgram?.productsAsGifts?.forEach(item => {
        const isAlreadyInArr = uniqueProducts?.find(
          product => product?.id === item.id
        );
        if (
          !isAlreadyInArr &&
          item.isAvailableForPurchase &&
          item.status &&
          item.status.id === 1
        ) {
          uniqueProducts.push({
            ...item,
            loyaltyItemId: loyaltyItem.id,
            quantity: Number(loyaltyItem.quantity)
          });
        }
      });
      loyaltyItem.loyaltyProgram?.servicesAsGifts?.forEach(item => {
        if (!uniqueServices.find(service => service.id === item.id)) {
          uniqueServices.push({
            ...item,
            cartLoyaltyProgramId: loyaltyItem.id,
            quantity: Number(loyaltyItem.quantity)
          });
        }
      });
    }

    if (loyaltyItem.quantity) {
      const gift = loyaltyItem.product
        ? {
            entity: 'product',
            quantity: loyaltyItem.quantity,
            id: Number(loyaltyItem.product.id)
          }
        : loyaltyItem.service
        ? {
            entity: 'service',
            quantity: loyaltyItem.quantity,
            id: Number(loyaltyItem.service.id)
          }
        : { quantity: loyaltyItem.quantity };

      activeGift.push(gift);
    }
  });

  const newGiftToSelect = {
    activeGift,
    uniqueProducts,
    uniqueServices
  };

  return newGiftToSelect;
};

export default prepareGiftToSelect;
